import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Button } from "reactstrap";
import {
  MdAssessment,
  MdCheckCircle,
  MdChevronRight,
  MdInsertDriveFile,
  MdPrint,
} from "react-icons/md";
import { useGetDocuments } from "api/documents/service";
import { useGetRecordSpecificReports } from "api/reports/service";
import { usePrintInventory } from "api/inventory/service";

const InventoryPrintDrawer = forwardRef(function InventoryPrintDrawer(
  {
    inventoryId,
    activeColorStyle,
    isLightTheme = false,
  },
  ref
) {
  const { t } = useTranslation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [printSubView, setPrintSubView] = useState("menu");
  const [sourceView, setSourceView] = useState("menu");
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [drawerFullSize, setDrawerFullSize] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
  const drawerWidthDragRef = useRef(null);
  const printInventoryMutation = usePrintInventory();

  const resetPrintDrawerState = useCallback(() => {
    setPrintSubView("menu");
    setSourceView("menu");
    setSelectedDocuments([]);
    setDrawerFullSize(false);
    setPdfUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  const shouldLoadReports = isDrawerVisible && printSubView === "reports";

  const { data: documentsData, isLoading: isDocumentsLoading } =
    useGetDocuments("inventory");
  const { data: recordReportsData, isLoading: isRecordReportsLoading } =
    useGetRecordSpecificReports("inventory", shouldLoadReports);

  const inventoryRecordReports = useMemo(
    () =>
      (recordReportsData?.results || []).map((report) => ({
        id: report.id,
        name: report.name,
      })),
    [recordReportsData?.results]
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 992);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    resetPrintDrawerState();
  }, [resetPrintDrawerState]);

  useImperativeHandle(
    ref,
    () => ({
      open: () => {
        resetPrintDrawerState();
        setIsDrawerVisible(true);
        setTimeout(() => setDrawerOpen(true), 0);
      },
      close: closeDrawer,
    }),
    [closeDrawer, resetPrintDrawerState]
  );

  const endDrawerWidthResizeInteraction = useCallback((clientX) => {
    const drag = drawerWidthDragRef.current;
    if (!drag) return;
    drawerWidthDragRef.current = null;
    const dx = clientX - drag.startX;
    if (Math.abs(dx) < 14) {
      setDrawerFullSize(!drag.startFull);
    } else if (dx < -42) {
      setDrawerFullSize(true);
    } else if (dx > 42) {
      setDrawerFullSize(false);
    }
  }, []);

  const onDrawerWidthHandlePointerDown = useCallback(
    (e) => {
      if (isMobile) return;
      e.preventDefault();
      e.stopPropagation();
      const el = e.currentTarget;
      if (typeof el.setPointerCapture === "function") {
        el.setPointerCapture(e.pointerId);
      }
      drawerWidthDragRef.current = {
        startX: e.clientX,
        startFull: drawerFullSize,
      };
      const onMove = () => {};
      const onUp = (ev) => {
        endDrawerWidthResizeInteraction(ev.clientX);
        try {
          if (typeof el.releasePointerCapture === "function") {
            el.releasePointerCapture(ev.pointerId);
          }
        } catch (_) {
          /* ignore */
        }
        el.removeEventListener("pointermove", onMove);
        el.removeEventListener("pointerup", onUp);
        el.removeEventListener("pointercancel", onUp);
      };
      el.addEventListener("pointermove", onMove, { passive: true });
      el.addEventListener("pointerup", onUp);
      el.addEventListener("pointercancel", onUp);
    },
    [isMobile, drawerFullSize, endDrawerWidthResizeInteraction]
  );

  const handleBack = useCallback(() => {
    if (printSubView === "viewer") {
      setPrintSubView(sourceView);
      setDrawerFullSize(false);
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
        setPdfUrl(null);
      }
      return;
    }
    setPrintSubView("menu");
  }, [printSubView, sourceView, pdfUrl]);

  const handleRecordReportClick = useCallback(
    (report) => {
      if (!inventoryId || !report?.id) return;
      const url = `/reports/${report.id}?inventory_id=${encodeURIComponent(
        inventoryId
      )}&view=true`;
      window.open(url, "_blank", "noopener,noreferrer");
      closeDrawer();
    },
    [inventoryId, closeDrawer]
  );

  const drawerStyles = useMemo(
    () => ({
      overlay: {
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "rgba(0,0,0,0.35)",
        zIndex: 2000,
        opacity: 0,
        transition: "opacity 300ms ease",
      },
      panel: {
        position: "fixed",
        top: 0,
        right: 0,
        width: isMobile ? "100%" : drawerFullSize ? "85vw" : "45vw",
        maxWidth: isMobile ? "100%" : "90vw",
        height: "100vh",
        backgroundColor: isLightTheme ? "#fff" : "#1e1e2f",
        color: isLightTheme ? "#1e1e2f" : "#fff",
        boxShadow: "-8px 0 24px rgba(0,0,0,0.4)",
        zIndex: 2001,
        display: "flex",
        flexDirection: "row",
        alignItems: "stretch",
        transform: "translateX(100%)",
        transition: "transform 300ms ease, width 220ms ease, max-width 220ms ease",
      },
      drawerResizeHandle: {
        width: 18,
        flexShrink: 0,
        cursor: "ew-resize",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        touchAction: "none",
        userSelect: "none",
        WebkitUserSelect: "none",
        borderRight: isLightTheme
          ? "1px solid rgba(0,0,0,0.08)"
          : "1px solid rgba(255,255,255,0.12)",
        backgroundColor: isLightTheme
          ? "rgba(0,0,0,0.03)"
          : "rgba(255,255,255,0.04)",
      },
      mainColumn: {
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      },
      header: {
        padding: isMobile ? "8px 12px" : "12px 16px",
        flexShrink: 0,
        borderBottom: isLightTheme
          ? "1px solid rgba(0,0,0,0.1)"
          : "1px solid rgba(255,255,255,0.1)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 8,
        minWidth: 0,
      },
      body: {
        padding: "8px 16px 16px 16px",
        overflow: "auto",
        flex: 1,
        minHeight: 0,
      },
      closeBtn: {
        ...activeColorStyle,
        color: "white",
        border: 0,
        padding: "6px 12px",
        fontWeight: 600,
        cursor: "pointer",
      },
    }),
    [activeColorStyle, isLightTheme, drawerFullSize, isMobile]
  );

  const headerTitle = useMemo(() => {
    if (printSubView === "viewer") {
      return t("inventoryPrintDrawer.documentViewer");
    }
    if (printSubView === "documents") {
      return t("inventoryPrintDrawer.printDocuments");
    }
    if (printSubView === "reports") {
      return t("inventoryPrintDrawer.recordSpecificReports");
    }
    return t("inventoryPrintDrawer.printOptions");
  }, [printSubView, t]);

  const renderPrintContent = () => {
    if (printSubView === "viewer" && pdfUrl) {
      return (
        <div
          className="pt-3 h-100 d-flex flex-column"
          style={{ height: "calc(100vh - 100px)" }}
        >
          <div
            className="flex-1"
            style={{ width: "100%", height: "100%", overflow: "hidden" }}
          >
            <iframe
              key={pdfUrl}
              id="inventory-pdf-iframe"
              src={pdfUrl}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                minHeight: "600px",
              }}
              title={t("inventoryPrintDrawer.documentViewer")}
            />
          </div>
        </div>
      );
    }

    if (printSubView === "documents") {
      const allDocs = documentsData?.data || [];
      const allIds = allDocs.map((d) => d.id);
      const isAllSelected =
        allIds.length > 0 && selectedDocuments.length === allIds.length;

      return (
        <div className="pt-3 h-100 d-flex flex-column">
          <div className="d-flex align-items-center mb-3">
            <div
              className="custom-control custom-checkbox pl-0 cursor-pointer d-flex align-items-center"
              onClick={() => {
                if (isAllSelected) setSelectedDocuments([]);
                else setSelectedDocuments(allIds);
              }}
            >
              <div
                style={{
                  width: 20,
                  height: 20,
                  border: `2px solid ${
                    isAllSelected
                      ? activeColorStyle.borderColor
                      : isLightTheme
                        ? "#ccc"
                        : "#666"
                  }`,
                  backgroundColor: isAllSelected
                    ? activeColorStyle.borderColor
                    : "transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: 10,
                  transition: "all 0.2s ease",
                }}
              >
                {isAllSelected && <MdCheckCircle size={16} color="#fff" />}
              </div>
              <span style={{ fontSize: 13, fontWeight: 600 }}>
                {t("inventoryPrintDrawer.selectDeselectAll")}
              </span>
            </div>
          </div>

          <div className="flex-1 overflow-auto" style={{ minHeight: "300px" }}>
            {isDocumentsLoading ? (
              <div className="text-center py-4">
                {t("inventoryPrintDrawer.loadingDocuments")}
              </div>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "10px",
                  width: "100%",
                }}
              >
                {allDocs.map((doc) => {
                  const isSelected = selectedDocuments.includes(doc.id);
                  const docName =
                    doc.attributes?.name || doc.name || "Untitled Document";
                  return (
                    <div
                      key={doc.id}
                      className="d-flex align-items-center p-2 cursor-pointer"
                      title={docName}
                      style={{
                        cursor: "pointer",
                        backgroundColor: isLightTheme
                          ? "rgba(0,0,0,0.02)"
                          : "rgba(255,255,255,0.02)",
                        border: `1px solid ${
                          isSelected
                            ? activeColorStyle.borderColor
                            : "transparent"
                        }`,
                        transition: "all 0.2s ease",
                        minWidth: 0,
                        width: "100%",
                      }}
                      onClick={() => {
                        setSelectedDocuments((prev) =>
                          prev.includes(doc.id)
                            ? prev.filter((id) => id !== doc.id)
                            : [...prev, doc.id]
                        );
                      }}
                    >
                      <div
                        style={{
                          minWidth: 20,
                          width: 20,
                          height: 20,
                          border: `2px solid ${
                            isSelected
                              ? activeColorStyle.borderColor
                              : isLightTheme
                                ? "#ccc"
                                : "#666"
                          }`,
                          backgroundColor: isSelected
                            ? activeColorStyle.borderColor
                            : "transparent",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          marginRight: 8,
                          transition: "all 0.2s ease",
                        }}
                      >
                        {isSelected && (
                          <MdCheckCircle size={16} color="#fff" />
                        )}
                      </div>
                      <span
                        style={{
                          fontSize: 13,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {docName}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
            {!isDocumentsLoading && allDocs.length === 0 && (
              <div className="text-center py-4 text-muted">
                {t("inventoryPrintDrawer.noDocumentsFound")}
              </div>
            )}
          </div>

          <div className="mt-3 d-flex justify-content-end">
            <Button
              style={{ ...activeColorStyle, color: "white" }}
              disabled={
                selectedDocuments.length === 0 ||
                printInventoryMutation.isPending ||
                !inventoryId
              }
              onClick={() => {
                printInventoryMutation.mutate(
                  {
                    id: inventoryId,
                    documents: selectedDocuments,
                  },
                  {
                    onSuccess: (response) => {
                      const blob = new Blob([response.data], {
                        type: "application/pdf",
                      });
                      const blobUrl = URL.createObjectURL(blob);
                      setPdfUrl(blobUrl);
                      setSourceView("documents");
                      setPrintSubView("viewer");
                      setDrawerFullSize(true);
                    },
                  }
                );
              }}
            >
              {printInventoryMutation.isPending
                ? t("inventoryPrintDrawer.printing")
                : t("inventoryPrintDrawer.printSelected")}
            </Button>
          </div>
        </div>
      );
    }

    if (printSubView === "reports") {
      return (
        <div className="h-100 d-flex flex-column">
          <div
            className="d-flex align-items-center justify-content-center font-weight-bold text-uppercase"
            style={{
              ...activeColorStyle,
              color: "white",
              fontSize: "13px",
              letterSpacing: "0.04em",
              marginTop: -8,
              marginLeft: -16,
              marginRight: -16,
              marginBottom: 12,
              padding: "8px 12px",
            }}
          >
            {t("inventoryPrintDrawer.recordSpecificReports")}
          </div>

          <div className="flex-1 overflow-auto" style={{ minHeight: "300px" }}>
            {isRecordReportsLoading ? (
              <div className="text-center py-4">{t("common.loading")}</div>
            ) : inventoryRecordReports.length === 0 ? (
              <div className="text-center py-4 text-muted">
                {t("inventoryPrintDrawer.noRecordSpecificReports")}
              </div>
            ) : (
              <div className="d-flex flex-column">
                {inventoryRecordReports.map((report) => (
                  <div
                    key={report.id}
                    className="d-flex align-items-center px-3 py-2 cursor-pointer border-bottom"
                    style={{
                      cursor: "pointer",
                      backgroundColor: isLightTheme
                        ? "#fff"
                        : "rgba(255,255,255,0.02)",
                      borderColor: isLightTheme
                        ? "#e9ecef"
                        : "rgba(255,255,255,0.08)",
                      transition: "background-color 0.2s ease",
                    }}
                    onClick={() => handleRecordReportClick(report)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = isLightTheme
                        ? "rgba(29, 140, 248, 0.08)"
                        : "rgba(255,255,255,0.06)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = isLightTheme
                        ? "#fff"
                        : "rgba(255,255,255,0.02)";
                    }}
                  >
                    <MdPrint
                      size={20}
                      style={{
                        color: activeColorStyle.borderColor,
                        marginRight: 12,
                        flexShrink: 0,
                      }}
                    />
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: isLightTheme ? "#1e1e2f" : "#fff",
                      }}
                    >
                      {report.name}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="d-flex flex-column gap-3 pt-3">
        {[
          {
            label: t("inventoryPrintDrawer.menu.documents"),
            icon: <MdInsertDriveFile size={22} />,
            id: "documents",
          },
          {
            label: t("inventoryPrintDrawer.menu.reports"),
            icon: <MdAssessment size={22} />,
            id: "reports",
          },
        ].map((item) => (
          <div
            key={item.id}
            className="p-3 mb-2 cursor-pointer d-flex align-items-center"
            onClick={() => setPrintSubView(item.id)}
            style={{
              backgroundColor: isLightTheme
                ? "rgba(0,0,0,0.05)"
                : "rgba(255,255,255,0.05)",
              cursor: "pointer",
              transition: "all 0.2s ease",
              border: "1px solid transparent",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = isLightTheme
                ? "rgba(0,0,0,0.08)"
                : "rgba(255,255,255,0.08)";
              e.currentTarget.style.borderColor = activeColorStyle.borderColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = isLightTheme
                ? "rgba(0,0,0,0.05)"
                : "rgba(255,255,255,0.05)";
              e.currentTarget.style.borderColor = "transparent";
            }}
          >
            <div
              style={{
                marginRight: "15px",
                display: "flex",
                alignItems: "center",
                opacity: 0.8,
              }}
            >
              {item.icon}
            </div>
            <h6
              className="mb-0"
              style={{
                fontWeight: 600,
                color: isLightTheme ? "#1e1e2f" : "#fff",
              }}
            >
              {item.label}
            </h6>
            <MdChevronRight
              size={20}
              style={{ marginLeft: "auto", opacity: 0.5 }}
            />
          </div>
        ))}
      </div>
    );
  };

  if (!isDrawerVisible) return null;

  return (
    <>
      <div
        style={{
          ...drawerStyles.overlay,
          opacity: drawerOpen ? 1 : 0,
          pointerEvents: drawerOpen ? "auto" : "none",
        }}
        onClick={closeDrawer}
      />
      <div
        style={{
          ...drawerStyles.panel,
          transform: drawerOpen ? "translateX(0)" : "translateX(100%)",
        }}
        onClick={(e) => e.stopPropagation()}
        onTransitionEnd={() => {
          if (!drawerOpen) {
            setIsDrawerVisible(false);
            resetPrintDrawerState();
          }
        }}
      >
        {!isMobile && (
          <div
            style={drawerStyles.drawerResizeHandle}
            onPointerDown={onDrawerWidthHandlePointerDown}
            title={t("accountsDrawer.drawerResizeTitle")}
            role="separator"
            aria-orientation="vertical"
            aria-label={t("accountsDrawer.drawerResizeAria")}
          >
            <div
              style={{
                width: 3,
                height: 40,
                backgroundColor: activeColorStyle.borderColor,
                opacity: 0.55,
              }}
            />
          </div>
        )}
        <div style={drawerStyles.mainColumn}>
          <div style={drawerStyles.header}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flex: 1,
                minWidth: 0,
              }}
            >
              {printSubView !== "menu" && (
                <button
                  type="button"
                  className="btn btn-link p-0 mr-3"
                  style={{
                    color: activeColorStyle.borderColor,
                    fontWeight: 600,
                    fontSize: 13,
                  }}
                  onClick={handleBack}
                >
                  {t("common.back")}
                </button>
              )}
              <h5
                className="mb-0 text-uppercase"
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  letterSpacing: "0.04em",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {headerTitle}
              </h5>
            </div>
            <button
              type="button"
              onClick={closeDrawer}
              style={drawerStyles.closeBtn}
              aria-label={t("common.close")}
            >
              <i className="tim-icons icon-simple-remove" />
            </button>
          </div>
          <div style={drawerStyles.body}>{renderPrintContent()}</div>
        </div>
      </div>
    </>
  );
});

export default InventoryPrintDrawer;
